import { useCallback, useEffect, useState } from "react";
import styled from "styled-components";
import { CardData, generateDisplayCard } from "../../utils/cardGenerator";

const Container = styled.div`
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 20px;
  padding: 20px;
  overflow-x: auto;

  &.card-display-large {
    padding: 0;
    gap: 0;
    width: 100%;
    height: 100%;
  }
`;

const PreviewContainer = styled.div`
  border: 1px solid #ddd;
  border-radius: 8px;
  padding: 20px;
  background-color: #f9f9f9;

  .card-display-large & {
    border: none;
    padding: 10px;
    background-color: white;
    width: 100%;
    display: flex;
    justify-content: center;
    align-items: center;
    flex: 1;
  }
`;

const CanvasPreview = styled.canvas`
  display: block;
  width: 100%;
  max-width: 400px;
  height: auto;
  border-radius: 8px;
  box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);

  .card-display-large & {
    width: 100%;
    max-width: min(90vw, 600px);
    height: auto;
    max-height: 75vh;
    object-fit: contain;
    border-radius: 12px;
    box-shadow: 0 4px 8px rgba(0, 0, 0, 0.1);

    @media (max-width: 768px) {
      max-width: 90vw;
      max-height: 70vh;
    }
  }
`;

const LoadingText = styled.p`
  color: #666;
  font-style: italic;
`;

interface MembershipCardProps {
  userName: string;
  backgroundImageUrl: string;
  className?: string;
  onCanvasReady?: (canvas: HTMLCanvasElement | null) => void;
}

export const MemberShipCard = ({
  userName,
  backgroundImageUrl,
  className,
  onCanvasReady,
}: MembershipCardProps) => {
  const [previewCanvas, setPreviewCanvas] = useState<HTMLCanvasElement | null>(
    null,
  );

  const generatePreview = useCallback(async () => {
    try {
      const cardData: CardData = {
        userName,
      };
      const canvas = await generateDisplayCard(cardData, backgroundImageUrl);
      setPreviewCanvas(canvas);
      onCanvasReady?.(canvas);
    } catch (error) {
      console.error("Error generating card preview:", error);
      alert("Error generating card preview. Please try again.");
      onCanvasReady?.(null);
    }
  }, [backgroundImageUrl, onCanvasReady, userName]);

  // Auto-generate preview on mount and window resize
  useEffect(() => {
    generatePreview();
  }, [generatePreview, userName]);

  // Regenerate on window resize for optimal sizing
  useEffect(() => {
    const handleResize = () => {
      generatePreview();
    };

    window.addEventListener("resize", handleResize);
    return () => window.removeEventListener("resize", handleResize);
  }, [generatePreview]);

  return (
    <Container className={className}>
      <PreviewContainer>
        {previewCanvas ? (
          <CanvasPreview
            ref={(canvasElement) => {
              if (canvasElement && previewCanvas) {
                const ctx = canvasElement.getContext("2d");
                if (ctx) {
                  canvasElement.width = previewCanvas.width;
                  canvasElement.height = previewCanvas.height;
                  ctx.drawImage(previewCanvas, 0, 0);
                }
              }
            }}
          />
        ) : (
          <LoadingText>Generating card preview...</LoadingText>
        )}
      </PreviewContainer>
    </Container>
  );
};
